import React, { createContext, useContext, useEffect, useState } from "react";
import { DayInfo } from "../types/daysInMonth";
import { generateDaysInMonth } from "../utils/generateDaysInMonth";

interface CalendarContextProps {
  activeDesktopCalendar: "origin" | "destination" | null;
  setActiveDesktopCalendar: (calendar: "origin" | "destination" | null) => void;
  selectionMode: "single" | "multi";
  setSelectionMode: (mode: "single" | "multi") => void;
  currentDate: Date;
  selectedOriginDates: string[];
  selectedDestinationDates: string[];
  daysInMonth: DayInfo[];
  setSelectedOriginDates: (dates: string[]) => void;
  setSelectedDestinationDates: (dates: string[]) => void;
  setCurrentDate: (date: Date) => void;
  isShowOriginCalendarPickerMobile: boolean;
  setShowOriginCalendarPickerMobile: (valeu: boolean) => void;
  isShowDestinationCalendarPickerMobile: boolean;
  setShowDestinationCalendarPickerMobile: (valeu: boolean) => void;
}

const CalendarContext = createContext<CalendarContextProps | undefined>(
  undefined,
);

export const CalendarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectionMode, setSelectionMode] = useState<"single" | "multi">(
    "single",
  );
  const [activeDesktopCalendar, setActiveDesktopCalendar] = useState<
    "origin" | "destination" | null
  >(null);
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0,
    );
  });
  const [selectedOriginDates, setSelectedOriginDates] = useState<string[]>([]);
  const [selectedDestinationDates, setSelectedDestinationDates] = useState<
    string[]
  >([]);
  const [daysInMonth, setDaysInMonth] = useState<DayInfo[]>([]);
  const [isShowOriginCalendarPickerMobile, setShowOriginCalendarPickerMobile] =
    useState<boolean>(false);

  const [
    isShowDestinationCalendarPickerMobile,
    setShowDestinationCalendarPickerMobile,
  ] = useState<boolean>(false);

  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    setDaysInMonth(
      generateDaysInMonth(
        year,
        month,
        selectedOriginDates,
        selectedDestinationDates,
      ),
    );
  }, [currentDate, selectedOriginDates, selectedDestinationDates]);

  return (
    <CalendarContext.Provider
      value={{
        activeDesktopCalendar,
        setActiveDesktopCalendar,
        selectionMode,
        setSelectionMode,
        currentDate,
        selectedOriginDates,
        selectedDestinationDates,
        daysInMonth,
        setSelectedOriginDates,
        setSelectedDestinationDates,
        setCurrentDate,
        isShowOriginCalendarPickerMobile,
        setShowOriginCalendarPickerMobile,
        isShowDestinationCalendarPickerMobile,
        setShowDestinationCalendarPickerMobile,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error(
      "useCalendarContext must be used within a CalendarProvider",
    );
  }
  return context;
};
